/**
 * Feedback API Client
 * Frontend API calls for viewing feedback (admin/caterer only)
 */

import { apiCall } from '../api';
import type { FeedbackListResponse } from '@/types/api';

/**
 * Options for fetching feedback
 */
export interface FetchFeedbackOptions {
  startDate: string;  // YYYY-MM-DD
  endDate: string;    // YYYY-MM-DD
  page?: number;
  pageSize?: number;
}

/**
 * Fetch all feedback for a date range (admin/caterer only)
 *
 * @param options - Filter options including date range
 * @returns Feedback data (flat list and grouped by date)
 */
export async function fetchFeedback(
  options: FetchFeedbackOptions
): Promise<FeedbackListResponse> {
  const params = new URLSearchParams({
    startDate: options.startDate,
    endDate: options.endDate,
  });

  if (options.page !== undefined) {
    params.append('page', options.page.toString());
  }
  if (options.pageSize !== undefined) {
    params.append('pageSize', options.pageSize.toString());
  }

  const response = await apiCall<FeedbackListResponse>(
    `/api/feedback?${params.toString()}`,
    { method: 'GET' }
  );

  return response;
}

/** Export the complete date range, independent of the page's pagination. */
export async function exportFeedbackXLSX(
  options: Pick<FetchFeedbackOptions, 'startDate' | 'endDate'> & { anonymous: boolean }
): Promise<void> {
  const params = new URLSearchParams({
    startDate: options.startDate,
    endDate: options.endDate,
    anonymous: String(options.anonymous),
    format: 'xlsx',
  });
  const response = await fetch(`/api/feedback?${params}`, { credentials: 'include' });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Export failed with status ${response.status}`);
  }
  const url = window.URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  try {
    link.href = url;
    link.download = `feedback-${options.startDate}-to-${options.endDate}.xlsx`;
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}
